import { initializeApp, getApp } from 'firebase/app'
import {
    getAnalytics,
    isSupported,
    logEvent,
    setUserId,
    setUserProperties
} from 'firebase/analytics'

const firebaseConfig = {
    apiKey: process.env.VUE_APP_FIREBASE_API_KEY || '',
    authDomain: process.env.VUE_APP_FIREBASE_AUTH_DOMAIN || '',
    projectId: process.env.VUE_APP_FIREBASE_PROJECT_ID || '',
    storageBucket: process.env.VUE_APP_FIREBASE_STORAGE_BUCKET || '',
    messagingSenderId: process.env.VUE_APP_FIREBASE_MESSAGING_SENDER_ID || '',
    appId: process.env.VUE_APP_FIREBASE_APP_ID || '',
    measurementId: process.env.VUE_APP_FIREBASE_MEASUREMENT_ID || ''
}

const requiredConfigKeys = [
    'apiKey',
    'authDomain',
    'projectId',
    'appId',
    'measurementId'
]

let analyticsPromise = null

function hasFirebaseAnalyticsConfig() {
    return requiredConfigKeys.every(key => Boolean(firebaseConfig[key]))
}

function getOrCreateApp() {
    try {
        return getApp()
    } catch (_err) {
        return initializeApp(firebaseConfig)
    }
}

async function getFirebaseAnalytics() {
    if (!hasFirebaseAnalyticsConfig()) {
        return null
    }

    if (!analyticsPromise) {
        analyticsPromise = isSupported()
            .then(supported => {
                if (!supported) return null
                return getAnalytics(getOrCreateApp())
            })
            .catch(err => {
                console.warn('Firebase Analytics is unavailable:', err)
                return null
            })
    }

    return analyticsPromise
}

function normalizeAnalyticsUser(user = {}) {
    return {
        userId: user.userId || '',
        username: user.username || '',
        email: user.email || ''
    }
}

export function initializeFirebaseAnalytics() {
    return getFirebaseAnalytics()
}

export async function setAnalyticsUser(user) {
    try {
        const analytics = await getFirebaseAnalytics()
        if (!analytics) return

        const normalizedUser = normalizeAnalyticsUser(user)

        if (!normalizedUser.userId) {
            await clearAnalyticsUser()
            return
        }

        setUserId(analytics, normalizedUser.userId)
        setUserProperties(analytics, {
            auth_state: 'authenticated',
            auth_provider: 'keycloak',
            has_email: normalizedUser.email ? 'true' : 'false',
            has_username: normalizedUser.username ? 'true' : 'false'
        })
    } catch (err) {
        console.warn('Failed to set Firebase Analytics user:', err)
    }
}

export async function clearAnalyticsUser() {
    try {
        const analytics = await getFirebaseAnalytics()
        if (!analytics) return

        setUserId(analytics, null)
        setUserProperties(analytics, {
            auth_state: 'anonymous',
            auth_provider: 'none',
            has_email: 'false',
            has_username: 'false'
        })
    } catch (err) {
        console.warn('Failed to clear Firebase Analytics user:', err)
    }
}

export async function syncAnalyticsUserFromStorage() {
    const authToken = localStorage.getItem('auth_token') || ''
    const userId = localStorage.getItem('userId') || ''

    if (!authToken || !userId) {
        await clearAnalyticsUser()
        return
    }

    await setAnalyticsUser({
        userId,
        username: localStorage.getItem('username') || '',
        email: localStorage.getItem('email') || ''
    })
}

export async function trackPageView(route) {
    try {
        const analytics = await getFirebaseAnalytics()
        if (!analytics) return

        const pagePath = route?.fullPath || window.location.pathname
        const pageName = route?.name || route?.path || pagePath

        logEvent(analytics, 'page_view', {
            page_path: pagePath,
            page_location: new URL(pagePath, window.location.origin).href,
            page_title: document.title,
            page_name: String(pageName)
        })
    } catch (err) {
        console.warn('Failed to track Firebase Analytics page view:', err)
    }
}
